import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  Button,
  IconButton,
  Badge,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Send as SendIcon,
  Message as MessageIcon,
  Business as BusinessIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import {
  getConversations,
  getMessages,
  sendMessage,
  markAllMessagesRead,
  getUnreadMessageCount,
} from '../services/messagingService';
import { getImageUrl } from '../utils/imageUtils';
import { getVendorProfile } from '../services/vendorService';
import { getOrganizerProfile } from '../services/eventService';

const Messages = () => {
  const theme = useTheme();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  
  // Check for conversation ID in URL params
  const urlParams = new URLSearchParams(window.location.search);
  const conversationIdFromUrl = urlParams.get('conversation');
  
  const [selectedConversationId, setSelectedConversationId] = useState(
    conversationIdFromUrl ? parseInt(conversationIdFromUrl) : null
  );
  const [messageText, setMessageText] = useState('');
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Fetch conversations
  const { data: conversations, isLoading: conversationsLoading } = useQuery(
    'conversations',
    getConversations,
    {
      refetchInterval: 5000, // Poll every 5 seconds for new messages
    }
  );

  // Fetch messages for selected conversation
  const { data: messages, isLoading: messagesLoading } = useQuery(
    ['messages', selectedConversationId],
    () => getMessages(selectedConversationId),
    {
      enabled: !!selectedConversationId,
      refetchInterval: 3000, // Poll every 3 seconds for new messages
      onSuccess: () => {
        // Auto-scroll to bottom when new messages arrive, but only if user is near bottom
        setTimeout(() => {
          if (messagesContainerRef.current) {
            const container = messagesContainerRef.current;
            const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
            // Only auto-scroll if user is already near the bottom
            if (isNearBottom) {
              scrollToBottom();
            }
          }
        }, 100);
      },
    }
  );

  // Get unread count
  const { data: unreadData } = useQuery(
    'unreadMessageCount',
    getUnreadMessageCount,
    {
      refetchInterval: 10000, // Poll every 10 seconds
    }
  );

  // Fetch current user's profile for displaying their own avatar
  const { data: currentUserVendorProfile } = useQuery(
    ['vendorProfile'],
    getVendorProfile,
    {
      enabled: !!currentUser && currentUser?.role_id === 1,
      retry: false,
    }
  );

  const { data: currentUserOrganizerProfile } = useQuery(
    ['organizerProfile'],
    getOrganizerProfile,
    {
      enabled: !!currentUser && currentUser?.role_id === 2,
      retry: false,
    }
  );

  const getCurrentUserProfilePicture = () => {
    if (currentUser?.role_id === 1 && currentUserVendorProfile?.profile_picture_url) {
      return currentUserVendorProfile.profile_picture_url;
    } else if (currentUser?.role_id === 2 && currentUserOrganizerProfile?.profile_picture_url) {
      return currentUserOrganizerProfile.profile_picture_url;
    }
    return null;
  };

  const selectedConversation = conversations?.find(
    (c) => c.conversation_id === selectedConversationId
  );

  // Send message mutation
  const sendMessageMutation = useMutation(
    (content) => sendMessage(selectedConversationId, content),
    {
      onSuccess: () => {
        setMessageText('');
        queryClient.invalidateQueries(['messages', selectedConversationId]);
        queryClient.invalidateQueries('conversations');
        queryClient.invalidateQueries('unreadMessageCount');
        // Scroll to bottom after sending a message
        setTimeout(() => scrollToBottom(), 200);
      },
    }
  );

  // Mark all as read mutation
  const markReadMutation = useMutation(
    () => markAllMessagesRead(selectedConversationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('conversations');
        queryClient.invalidateQueries('unreadMessageCount');
      },
    }
  );

  const scrollToBottom = () => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      // Scroll within the messages container, not the whole page
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (selectedConversationId && messages) {
      // Only scroll to bottom if there are messages and user is already at or near the bottom
      // This prevents auto-scrolling when user is reading older messages
      if (messages.length > 0) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          if (messagesContainerRef.current) {
            const container = messagesContainerRef.current;
            const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
            // Only auto-scroll if user is already near the bottom (within 200px)
            if (isNearBottom || messages.length <= 5) {
              scrollToBottom();
            }
          }
        }, 100);
      }
      // Mark messages as read when viewing
      markReadMutation.mutate();
    }
  }, [selectedConversationId, messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedConversationId) return;
    sendMessageMutation.mutate(messageText.trim());
  };

  const handleSelectConversation = (conversationId) => {
    setSelectedConversationId(conversationId);
  };

  const getOtherPartyName = (conversation) => {
    if (currentUser?.role_id === 1) {
      // Vendor viewing - show organizer name
      return conversation.organizer_name || 'Organizer';
    } else {
      // Organizer viewing - show vendor name
      return conversation.vendor_name || 'Vendor';
    }
  };

  const getOtherPartyAvatar = (conversation) => {
    let profilePictureUrl = null;
    if (currentUser?.role_id === 1) {
      // Vendor viewing - show organizer profile picture
      profilePictureUrl = conversation.organizer_profile_picture_url;
    } else {
      // Organizer viewing - show vendor profile picture
      profilePictureUrl = conversation.vendor_profile_picture_url;
    }
    
    if (profilePictureUrl) {
      return (
        <Avatar 
          src={getImageUrl(profilePictureUrl)} 
          alt={getOtherPartyName(conversation)}
          sx={{ width: 40, height: 40 }}
        />
      );
    }
    
    // Fallback to icon if no profile picture
    return <BusinessIcon />;
  };

  if (conversationsLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Messages
      </Typography>

      <Paper elevation={2} sx={{ height: 'calc(100vh - 200px)', display: 'flex', overflow: 'hidden' }}>
        {/* Conversations List */}
        <Box
          sx={{
            width: 350,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Conversations
            </Typography>
            {unreadData?.unread_count > 0 && (
              <Chip
                label={`${unreadData.unread_count} unread`}
                color="primary"
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {!conversations || conversations.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <MessageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  No conversations yet
                </Typography>
              </Box>
            ) : (
              <List>
                {conversations.map((conversation) => (
                  <ListItem
                    key={conversation.conversation_id}
                    button
                    selected={selectedConversationId === conversation.conversation_id}
                    onClick={() => handleSelectConversation(conversation.conversation_id)}
                    sx={{
                      '&.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                        },
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        badgeContent={conversation.unread_count}
                        color="error"
                        invisible={conversation.unread_count === 0}
                      >
                        {(() => {
                          let profilePictureUrl = null;
                          if (currentUser?.role_id === 1) {
                            profilePictureUrl = conversation.organizer_profile_picture_url;
                          } else {
                            profilePictureUrl = conversation.vendor_profile_picture_url;
                          }
                          
                          if (profilePictureUrl) {
                            return (
                              <Avatar 
                                src={getImageUrl(profilePictureUrl)} 
                                alt={getOtherPartyName(conversation)}
                                sx={{ bgcolor: 'primary.main' }}
                              />
                            );
                          }
                          
                          return (
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {getOtherPartyAvatar(conversation)}
                            </Avatar>
                          );
                        })()}
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: conversation.unread_count > 0 ? 600 : 400 }}>
                            {getOtherPartyName(conversation)}
                          </Typography>
                          {conversation.event_title && (
                            <Chip
                              label={conversation.event_title}
                              size="small"
                              icon={<EventIcon />}
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          {conversation.last_message && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontWeight: conversation.unread_count > 0 ? 500 : 400,
                              }}
                            >
                              {conversation.last_message.content}
                            </Typography>
                          )}
                          {conversation.last_message && (
                            <Typography variant="caption" color="text.secondary">
                              {formatDistanceToNow(new Date(conversation.last_message.created_at), {
                                addSuffix: true,
                              })}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>

        {/* Chat Interface */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!selectedConversationId ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <MessageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Select a conversation to start messaging
              </Typography>
            </Box>
          ) : (
            <>
              {/* Chat Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {(() => {
                    let profilePictureUrl = null;
                    if (currentUser?.role_id === 1) {
                      profilePictureUrl = selectedConversation?.organizer_profile_picture_url;
                    } else {
                      profilePictureUrl = selectedConversation?.vendor_profile_picture_url;
                    }
                    
                    if (profilePictureUrl) {
                      return (
                        <Avatar 
                          src={getImageUrl(profilePictureUrl)} 
                          alt={getOtherPartyName(selectedConversation)}
                          sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}
                        />
                      );
                    }
                    
                    return (
                      <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                        {getOtherPartyAvatar(selectedConversation)}
                      </Avatar>
                    );
                  })()}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {getOtherPartyName(selectedConversation)}
                    </Typography>
                    {selectedConversation?.event_title && (
                      <Typography variant="body2" color="text.secondary">
                        Event: {selectedConversation.event_title}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Messages */}
              <Box
                ref={messagesContainerRef}
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: 2,
                  bgcolor: alpha(theme.palette.grey[50], 0.5),
                }}
              >
                {messagesLoading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : !messages || messages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No messages yet. Start the conversation!
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {messages.map((message) => {
                      const isOwnMessage = message.sender_id === currentUser?.id;
                      return (
                        <Box
                          key={message.message_id}
                          sx={{
                            display: 'flex',
                            justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                            alignItems: 'flex-end',
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          {!isOwnMessage && (
                            <Avatar
                              src={message.sender_profile_picture_url ? getImageUrl(message.sender_profile_picture_url) : null}
                              sx={{ 
                                width: 32, 
                                height: 32,
                                bgcolor: 'primary.main',
                                flexShrink: 0
                              }}
                            >
                              {!message.sender_profile_picture_url && <BusinessIcon />}
                            </Avatar>
                          )}
                          <Box
                            sx={{
                              maxWidth: '70%',
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: isOwnMessage
                                ? 'primary.main'
                                : 'background.paper',
                              color: isOwnMessage ? 'white' : 'text.primary',
                              boxShadow: 1,
                            }}
                          >
                            <Typography variant="body1">{message.content}</Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                opacity: 0.7,
                                fontSize: '0.7rem',
                              }}
                            >
                              {format(new Date(message.created_at), 'h:mm a')}
                            </Typography>
                          </Box>
                          {isOwnMessage && (
                            <Avatar
                              src={getCurrentUserProfilePicture() ? getImageUrl(getCurrentUserProfilePicture()) : null}
                              sx={{ 
                                width: 32, 
                                height: 32,
                                bgcolor: 'primary.main',
                                flexShrink: 0
                              }}
                            >
                              {!getCurrentUserProfilePicture() && <BusinessIcon />}
                            </Avatar>
                          )}
                        </Box>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </Box>

              {/* Message Input */}
              <Box
                sx={{
                  p: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <form onSubmit={handleSendMessage}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      placeholder="Type a message..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      disabled={sendMessageMutation.isLoading}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e);
                        }
                      }}
                    />
                    <IconButton
                      type="submit"
                      color="primary"
                      disabled={!messageText.trim() || sendMessageMutation.isLoading}
                      sx={{ alignSelf: 'flex-end' }}
                    >
                      {sendMessageMutation.isLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <SendIcon />
                      )}
                    </IconButton>
                  </Box>
                </form>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default Messages;

